import {filterMap} from '@benchling/js-utils';
import classNames from 'classnames';
import React, {useState} from 'react';
import _ from 'underscore';

import {pluralize} from 'BenchlingUtil';
import Annotation from 'molbio/annotations/backbone/Annotation';
import Modal from 'ui-library/modals/Modal';
import {EN_DASH} from 'util/UnicodeSymbols';

export interface DuplicateGroup {
  colors: Array<string>;
  count: number;
  deleteIds: Array<number>;
  end: number;
  name: string;
  start: number;
  strand: string;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const annotationIdentityKeyFn = (annotation: Annotation) =>
  `${annotation.strand()}-${annotation.start()}-${annotation.end()}-${annotation.name()}`;

interface FindDuplicateAnnotationsResult {
  resolvableGroups: Array<DuplicateGroup>;
  numUnresolvableGroups: number;
}

/**
 * Returns a list of resolvableGroups of duplicate annotations that can be pruned, and numUnresolvableGroups,
 * the count of duplicate groups that cannot be pruned because they are all hoisted from parts. Annotations
 * are grouped by the combination of start/end/strand/name. If a group contains both hoisted and non-hoisted
 * annotations, only the latter will ever be included in deleteIds.
 */
export function findDuplicateAnnotations(annotations: Array<Annotation>): FindDuplicateAnnotationsResult {
  const grouped = _.groupBy(annotations, annotationIdentityKeyFn);
  const duplicates = filterMap(grouped, (group) => group.length > 1);

  const resolvableGroups: Array<DuplicateGroup> = [];
  let numUnresolvableGroups = 0;
  for (const group of Object.values(duplicates)) {
    const [normal, fromPart] = _.partition(group, (a) => !a.get('_annotation').fromSubPart());
    if (normal.length === 0) {
      numUnresolvableGroups += 1;
      continue;
    }
    const toDelete = fromPart.length > 0 ? normal : normal.slice(0, normal.length - 1);
    const deleteIds = toDelete.map((a) => a.get('id'));
    const strand = group[0].strand();
    resolvableGroups.push({
      name: group[0].name(),
      start: group[0].start(),
      end: group[0].end(),
      strand: strand === -1 ? EN_DASH : strand === 1 ? '+' : '',
      colors: group.slice(0, 4).map((annotation) => annotation.color()),
      count: group.length,
      deleteIds,
    });
  }
  return {resolvableGroups, numUnresolvableGroups};
}

interface CleanDuplicateAnnotationsModalProps {
  resolvableGroups: Array<DuplicateGroup>;
  numUnresolvableGroups: number;
  onRequestClose: () => void;
  onRequestSubmit: (idsToDelete: Array<number>) => Promise<void>;
}

export default function CleanDuplicateAnnotationsModal({
  resolvableGroups,
  numUnresolvableGroups,
  onRequestClose,
  onRequestSubmit,
}: CleanDuplicateAnnotationsModalProps): JSX.Element {
  const [groupStatus, setGroupStatus] = useState(resolvableGroups.map(() => true));
  const activeCount = groupStatus.filter((s) => s).length;
  const idsToDelete = resolvableGroups.filter((_g, i) => groupStatus[i]).flatMap((group) => group.deleteIds);

  return (
    <Modal
      title="Clean Duplicate Annotations"
      onRequestClose={onRequestClose}
      onRequestSubmit={() => {
        onRequestSubmit(idsToDelete);
      }}
      buttons={[
        {
          text: 'Cancel',
          cancel: true,
        },
        {
          text: 'Submit',
          submit: true,
          disabled: idsToDelete.length === 0,
        },
      ]}
    >
      <div className="clean-duplicate-annotations">
        <p>
          The following duplicate annotation groups were found.{' '}
          <strong>
            {activeCount} {pluralize(activeCount, 'group')}{' '}
          </strong>{' '}
          will be cleaned (all but one annotation will be removed).
          {numUnresolvableGroups > 0 && (
            <span>
              {' '}
              An additional {numUnresolvableGroups} {pluralize(numUnresolvableGroups, 'group')} composed
              entirely of annotations from parts will be ignored.
            </span>
          )}
        </p>
        <ul className="list-group">
          {resolvableGroups.map((group, i) => (
            <li
              key={i}
              onClick={() => {
                setGroupStatus((oldStatus) => {
                  const newStatus = oldStatus.slice();
                  newStatus[i] = !oldStatus[i];
                  return newStatus;
                });
              }}
              className={classNames('list-group-item', {active: groupStatus[i]})}
            >
              <span className="badge">{group.count}</span>
              <div className="pull-right duplicate-colors">
                {group.count > 4 && <div className="more-colors">&hellip;</div>}
                {group.colors.map((color, j) => (
                  <div key={j} style={{backgroundColor: color}} className="duplicate-color" />
                ))}
              </div>
              <strong>{group.name}</strong>&nbsp;&nbsp;({group.strand} {group.start}-{group.end})
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}
